import { defineComponent as _defineComponent } from 'vue'
import { cloneDeep } from 'lodash';
import { now } from '@/utils/datetime';
import { PropType, ref, reactive, onMounted, computed } from 'vue';

import {
  SdsProduct,
  SdsSection3,
  SdsSection3ImpurityMaterial,
} from '@/interfaces/model/general/sds';
import { NewSdsSection3Impurity } from '@/lib/constructor/model/general/sds';
import SdsSection3ImpurityForm from '@/views/dashboard/sdsDrawer/forms/SdsSection3ImpurityForm.vue';
import { VForm } from 'vuetify/lib/components';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { useParentImpurities } from '@/composables/useParentImpurities';
import { generateTempImpurityId } from '@/utils/uuid';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsTableSection3Impurity',
  props: {
  isEditing: {
    type: Boolean,
    default: false,
  },
  section3: {
    type: Object as PropType<SdsSection3>,
    required: true,
  },
  productIndex: {
    type: Number,
    default: 0,
  },
  products: {
    type: Array as PropType<Array<SdsProduct>>,
    default: () => [],
  },
},
  emits: ["update:section3", "update:products"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:section3', data: SdsSection3): void;
  (e: 'update:products', data: Array<SdsProduct>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);



const headerSection3 = [
  {
    text: $t(sharedMessages.chemical),
    value: 'materialName',
    sortable: false,
  },
  { text: $t(sharedMessages.casNumber), value: 'casNumber', sortable: false },
  { text: $t(sharedMessages.encsNumber), value: 'encsNumber', sortable: false },
  { text: $t(sharedMessages.ishlNumber), value: 'ishlNumber', sortable: false },
  {
    text: $t(sharedMessages.concentration),
    value: 'concentration',
    sortable: false,
  },
  {
    text: $t(sharedMessages.parentIndicator), // Parent indicator
    value: 'hasParent',
    sortable: false,
    width: '80px',
  },
  {
    text: $t(sharedMessages.parentMaterial), // Parent name
    value: 'parentMaterialName',
    sortable: false,
    width: '200px',
  },
  { text: $t(sharedMessages.isStated), value: 'is_stated', sortable: false },
  { text: '', value: 'action', sortable: false },
];

const isImpurityConcentrationSecret = ref<boolean>(false);
const isValidUpdateForm = ref<boolean>(false);
const isValidAddForm = ref<boolean>(false);
const form = ref<InstanceType<typeof VForm> | null>(null);
const section3Index = ref<number>(0);
const modalAddSection3Impurity = ref<boolean>(false);
const modalRescueSection3 = ref<boolean>(false);
const modalDeleteSection3 = ref<boolean>(false);
const modalEditSection3Impurity = ref<boolean>(false);
const newImpurity = reactive<SdsSection3ImpurityMaterial>({
  ...cloneDeep(NewSdsSection3Impurity),
  sds_id: props.section3.sds_id,
  sds_section3_id: props.section3.id,
  concentration_unit: '%',
});
const updatedImpurity = reactive<SdsSection3ImpurityMaterial>({
  ...NewSdsSection3Impurity,
});

// Parent impurities composable integration
const impuritiesRef = computed(() => props.section3.impurity_list);
const materialsRef = computed(() => props.section3.material_list);
const { enrichedImpurities } = useParentImpurities(impuritiesRef, materialsRef);

const openAddSection3ImpurityModal = (): void => {
  modalAddSection3Impurity.value = true;
};

const closeAddSection3ImpurityModal = (): void => {
  modalAddSection3Impurity.value = false;
};

const openDeleteSection3Modal = (): void => {
  modalDeleteSection3.value = true;
};

const closeDeleteSection3Modal = (): void => {
  modalDeleteSection3.value = false;
};

const openRescueSection3Modal = (): void => {
  modalRescueSection3.value = true;
};

const closeRescueSection3Modal = (): void => {
  modalRescueSection3.value = false;
};

const openEditSection3ImpurityModal = (): void => {
  modalEditSection3Impurity.value = true;
};

const closeEditSection3ImpurityModal = (): void => {
  modalEditSection3Impurity.value = false;
};



const bindUpdatedImpurity = (impurity: SdsSection3ImpurityMaterial): void => {
  updatedImpurity.id = impurity.id;
  updatedImpurity.sds_id = impurity.sds_id;
  updatedImpurity.sds_section3_id = impurity.sds_section3_id;
  updatedImpurity.material_name = impurity.material_name;
  updatedImpurity.concentration = impurity.concentration;
  updatedImpurity.encs_number = impurity.encs_number;
  updatedImpurity.ishl_number = impurity.ishl_number;
  updatedImpurity.concentration_min = impurity.concentration_min;
  updatedImpurity.concentration_max = impurity.concentration_max;
  updatedImpurity.concentration_unit = impurity.concentration_unit;
  updatedImpurity.concentration_inequality_sign =
    impurity.concentration_inequality_sign;
  updatedImpurity.is_stated = impurity.is_stated;
  updatedImpurity.cas_list = cloneDeep(impurity.cas_list);
  updatedImpurity.created_at = impurity.created_at;
  updatedImpurity.updated_at = impurity.updated_at;
  updatedImpurity.deleted_at = impurity.deleted_at;

  // Bind parent impurity fields
  updatedImpurity.parent_impurity_material_id =
    impurity.parent_impurity_material_id;
  updatedImpurity.temp_impurity_material_id =
    impurity.temp_impurity_material_id;
  updatedImpurity.temp_parent_impurity_material_id =
    impurity.temp_parent_impurity_material_id;

  isImpurityConcentrationSecret.value =
    impurity.concentration_min == null && impurity.concentration_max == null;
};

const concentrationText = (impurity: SdsSection3ImpurityMaterial): string => {
  let text = '';

  switch (impurity.concentration_inequality_sign) {
    case 0:
      text = $t(sharedMessages.nonenPublic);
      break;
    case 1:
      text =
        impurity.concentration_min +
        impurity.concentration_unit +
        $t(sharedMessages.equal);
      break;
    case 2:
      text =
        impurity.concentration_min +
        impurity.concentration_unit +
        $t(sharedMessages.greaterThanOrEqual);
      break;
    case 3:
      text =
        impurity.concentration_min +
        impurity.concentration_unit +
        $t(sharedMessages.greaterThan);
      break;
    case 4:
      text =
        impurity.concentration_max +
        impurity.concentration_unit +
        $t(sharedMessages.lessThanOrEqual);
      break;
    case 5:
      text =
        impurity.concentration_max +
        impurity.concentration_unit +
        $t(sharedMessages.lessThan);
      break;
    case 6:
      text =
        impurity.concentration_min +
        impurity.concentration_unit +
        $t(sharedMessages.greaterThanOrEqual) +
        impurity.concentration_max +
        impurity.concentration_unit +
        $t(sharedMessages.lessThanOrEqual);
      break;
    case 7:
      text =
        impurity.concentration_min +
        impurity.concentration_unit +
        $t(sharedMessages.greaterThanOrEqual) +
        impurity.concentration_max +
        impurity.concentration_unit +
        $t(sharedMessages.lessThan);
      break;
    case 8:
      text =
        impurity.concentration_min +
        impurity.concentration_unit +
        $t(sharedMessages.greaterThan) +
        impurity.concentration_max +
        impurity.concentration_unit +
        $t(sharedMessages.lessThanOrEqual);
      break;
    case 9:
      text =
        impurity.concentration_min +
        impurity.concentration_unit +
        $t(sharedMessages.greaterThan) +
        impurity.concentration_max +
        impurity.concentration_unit +
        $t(sharedMessages.lessThan);
      break;
    default:
      text = $t(sharedMessages.nonenPublic);
      break;
  }

  return text;
};

const clearNewImpurity = (): void => {
  newImpurity.material_name = '';
  newImpurity.concentration = '';
  newImpurity.concentration_inequality_sign = 0;
  newImpurity.concentration_min = 0;
  newImpurity.concentration_max = 0;
  newImpurity.concentration_unit = '%';
  newImpurity.encs_number = '';
  newImpurity.ishl_number = '';
  newImpurity.is_stated = false;
  newImpurity.cas_list = [];
  newImpurity.deleted_at = null;

  // Clear and regenerate parent impurity fields
  newImpurity.parent_impurity_material_id = null;
  newImpurity.temp_impurity_material_id = generateTempImpurityId();
  newImpurity.temp_parent_impurity_material_id = undefined;
  newImpurity.hasParent = false;
  newImpurity.parentMaterialName = '';
  newImpurity.isParentDeleted = false;
};

const updateImpurity = (
  impurity: SdsSection3ImpurityMaterial,
  index: number
): void => {
  const section3 = { ...props.section3 };
  section3.impurity_list[index].id = impurity.id;
  section3.impurity_list[index].sds_id = impurity.sds_id;
  section3.impurity_list[index].sds_section3_id = impurity.sds_section3_id;
  section3.impurity_list[index].material_name = impurity.material_name;
  section3.impurity_list[index].concentration = impurity.concentration;
  section3.impurity_list[index].concentration_inequality_sign =
    impurity.concentration_inequality_sign;
  section3.impurity_list[index].concentration_min = impurity.concentration_min;
  section3.impurity_list[index].concentration_max = impurity.concentration_max;
  section3.impurity_list[index].concentration_unit =
    impurity.concentration_unit;
  section3.impurity_list[index].encs_number = impurity.encs_number;
  section3.impurity_list[index].ishl_number = impurity.ishl_number;
  section3.impurity_list[index].parent_impurity_material_id =
    impurity.parent_impurity_material_id;
  section3.impurity_list[index].temp_impurity_material_id =
    impurity.temp_impurity_material_id;
  section3.impurity_list[index].temp_parent_impurity_material_id =
    impurity.temp_parent_impurity_material_id;

  emits('update:section3', section3);
};

const rescueSection3 = (): void => {
  const data = props.section3;
  data.impurity_list[section3Index.value].deleted_at = null;
  for (const cas of data.impurity_list[section3Index.value].cas_list) {
    cas.deleted_at = null;
  }

  emits('update:section3', data);
};

const deleteSection3 = (): void => {
  const data = props.section3;
  data.impurity_list[section3Index.value].deleted_at = now().toString();
  for (const cas of data.impurity_list[section3Index.value].cas_list) {
    cas.deleted_at = now().toString();
  }

  emits('update:section3', data);
};

const addImpurity = (): void => {
  // Ensure new impurity has a temp_impurity_material_id before adding
  if (!newImpurity.temp_impurity_material_id) {
    newImpurity.temp_impurity_material_id = generateTempImpurityId();
  }

  const updatedData = structuredClone(props.section3);
  switch (newImpurity.concentration_inequality_sign) {
    case 0:
      newImpurity.concentration_min = null;
      newImpurity.concentration_max = null;
      break;
    case 1:
      newImpurity.concentration_max = newImpurity.concentration_min;
      break;
    case 2:
      newImpurity.concentration_max = 100;
      break;
    case 3:
      newImpurity.concentration_max = 100;
      break;
    case 4:
      newImpurity.concentration_min = 0;
      break;
    case 5:
      newImpurity.concentration_min = 0;
      break;
    case 6:
      break;
    case 7:
      break;
    case 8:
      break;
    case 9:
      break;
    default:
      break;
  }

  updatedData.impurity_list.push(structuredClone(newImpurity));
  emits('update:section3', updatedData);
};

onMounted((): void => {
  if (form.value) {
    form.value.validate();
  }
});

return { __sfc: true,$t, props, headerSection3, isImpurityConcentrationSecret, isValidUpdateForm, isValidAddForm, form, section3Index, modalAddSection3Impurity, modalRescueSection3, modalDeleteSection3, modalEditSection3Impurity, newImpurity, updatedImpurity, impuritiesRef, materialsRef, enrichedImpurities, openAddSection3ImpurityModal, closeAddSection3ImpurityModal, openDeleteSection3Modal, closeDeleteSection3Modal, openRescueSection3Modal, closeRescueSection3Modal, openEditSection3ImpurityModal, closeEditSection3ImpurityModal, emits, bindUpdatedImpurity, concentrationText, clearNewImpurity, updateImpurity, rescueSection3, deleteSection3, addImpurity, SdsSection3ImpurityForm, sharedMessages }
}

})